import type { Stage } from '@/types/adventurer'

export const PROGRESS_STATUS = {
  NOT_STARTED: 'NOT_STARTED',
  IN_PROGRESS: 'IN_PROGRESS',
  PASSED: 'PASSED',
  FAILED: 'FAILED',
  FIRST_REGULAR_MOCK_SCHEDULED: 'FIRST_REGULAR_MOCK_SCHEDULED',
  FIRST_REGULAR_MOCK_PASSED: 'FIRST_REGULAR_MOCK_PASSED',
  FIRST_REGULAR_MOCK_DIDNT_PASS: 'FIRST_REGULAR_MOCK_DIDNT_PASS',
  SECOND_REGULAR_MOCK_SCHEDULED: 'SECOND_REGULAR_MOCK_SCHEDULED',
  SECOND_REGULAR_MOCK_PASSED: 'SECOND_REGULAR_MOCK_PASSED',
  SECOND_REGULAR_MOCK_DIDNT_PASS: 'SECOND_REGULAR_MOCK_DIDNT_PASS',
  ADDITIONAL_REGULAR_MOCK_SCHEDULED: 'ADDITIONAL_REGULAR_MOCK_SCHEDULED',
  ADDITIONAL_REGULAR_MOCK_PASSED: 'ADDITIONAL_REGULAR_MOCK_PASSED',
  ADDITIONAL_REGULAR_MOCK_DIDNT_PASS: 'ADDITIONAL_REGULAR_MOCK_DIDNT_PASS',
  MAIN_MOCK_SCHEDULED: 'MAIN_MOCK_SCHEDULED',
  MAIN_MOCK_PASSED: 'MAIN_MOCK_PASSED',
  MAIN_MOCK_DIDNT_PASS: 'MAIN_MOCK_DIDNT_PASS'
} as const

export const progressStatusLabels: Record<string, string> = {
  [PROGRESS_STATUS.NOT_STARTED]: 'Not Started',
  [PROGRESS_STATUS.IN_PROGRESS]: 'In Progress',
  [PROGRESS_STATUS.PASSED]: 'Passed',
  [PROGRESS_STATUS.FAILED]: 'Failed',
  [PROGRESS_STATUS.FIRST_REGULAR_MOCK_SCHEDULED]: 'First Regular Mock Scheduled',
  [PROGRESS_STATUS.FIRST_REGULAR_MOCK_PASSED]: 'First Regular Mock Passed',
  [PROGRESS_STATUS.FIRST_REGULAR_MOCK_DIDNT_PASS]: "First Regular Mock Didn't Pass",
  [PROGRESS_STATUS.SECOND_REGULAR_MOCK_SCHEDULED]: 'Second Regular Mock Scheduled',
  [PROGRESS_STATUS.SECOND_REGULAR_MOCK_PASSED]: 'Second Regular Mock Passed',
  [PROGRESS_STATUS.SECOND_REGULAR_MOCK_DIDNT_PASS]: "Second Regular Mock Didn't Pass",
  [PROGRESS_STATUS.ADDITIONAL_REGULAR_MOCK_SCHEDULED]: 'Additional Regular Mock Scheduled',
  [PROGRESS_STATUS.ADDITIONAL_REGULAR_MOCK_PASSED]: 'Additional Regular Mock Passed',
  [PROGRESS_STATUS.ADDITIONAL_REGULAR_MOCK_DIDNT_PASS]: "Additional Regular Mock Didn't Pass",
  [PROGRESS_STATUS.MAIN_MOCK_SCHEDULED]: 'Main Mock Scheduled',
  [PROGRESS_STATUS.MAIN_MOCK_PASSED]: 'Main Mock Passed',
  [PROGRESS_STATUS.MAIN_MOCK_DIDNT_PASS]: "Main Mock Didn't Pass"
}

// Keep for backwards compatibility
export const progressStatusChoices = {
  [PROGRESS_STATUS.NOT_STARTED]: 'Not Started',
  [PROGRESS_STATUS.IN_PROGRESS]: 'In Progress',
  [PROGRESS_STATUS.PASSED]: 'Passed'
} as const

/**
 * Formats a progress status value to a human-readable label
 * Falls back to converting SNAKE_CASE to Title Case if status is not in the mapping
 */
export const formatProgressStatus = (status: string | null | undefined): string => {
  if (!status) return 'N/A'
  
  // Check if we have a predefined label
  if (progressStatusLabels[status]) {
    return progressStatusLabels[status]
  }
  
  // Fallback: Convert SNAKE_CASE to Title Case
  return status
    .toLowerCase()
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

/**
 * Returns CSS classes for styling a progress status badge
 */
export const getProgressStatusBadgeClass = (status: string | null | undefined): string => {
  if (!status) return 'bg-gray-100 text-gray-600'
  
  const statusUpper = status.toUpperCase()
  
  // Passed statuses - green
  if (statusUpper === 'PASSED' || statusUpper.includes('_PASSED')) {
    return 'bg-lime-100 text-lime-700'
  }
  
  // Failed/didn't pass statuses - red
  if (statusUpper === 'FAILED' || statusUpper.includes('DIDNT_PASS')) {
    return 'bg-red-100 text-red-700'
  }
  
  // Scheduled/in progress statuses - blue
  if (statusUpper === 'IN_PROGRESS' || statusUpper.includes('_SCHEDULED')) {
    return 'bg-blue-100 text-blue-700'
  }
  
  // Not started - gray
  if (statusUpper === 'NOT_STARTED') {
    return 'bg-gray-100 text-gray-600'
  }
  
  return 'bg-gray-100 text-gray-600'
}

/**
 * Returns the icon type for a progress status badge
 * Returns 'check' for passed statuses, 'x' for failed, 'loader' for in-progress/scheduled, null otherwise
 */
export const getProgressStatusIcon = (status: string | null | undefined): 'check' | 'x' | 'loader' | null => {
  if (!status) return null
  
  const statusUpper = status.toUpperCase()
  
  // Only show checkmark for PASSED or MAIN_MOCK_PASSED
  if (statusUpper === 'PASSED' || statusUpper === 'MAIN_MOCK_PASSED') {
    return 'check'
  }
  
  // Failed/didn't pass statuses - X icon
  if (statusUpper === 'FAILED' || statusUpper.includes('DIDNT_PASS')) {
    return 'x'
  }
  
  // Scheduled/in progress statuses - loader
  if (statusUpper === 'IN_PROGRESS' || statusUpper.includes('_SCHEDULED')) {
    return 'loader'
  }
  
  return null
}

export const stageInfo = {
  resume: {
    title: 'Resume',
    icon: 'FileBadge'
  },
  behavioral: {
    title: 'Behavioral',
    icon: 'Speech'
  },
  coding: {
    title: 'Coding',
    icon: 'SquareCode'
  },
  system_design: {
    title: 'System Design',
    icon: 'Cog'
  }
} as const

export type ProgressStatusType = keyof typeof PROGRESS_STATUS
export type StageType = keyof typeof stageInfo

export const getStageTitle = (stage: Stage): string => {
  return stageInfo[stage as StageType]?.title || stage
}

export const getStageIcon = (stage: Stage): string => {
  return stageInfo[stage as StageType]?.icon || 'FileBadge'
}

// eslint-disable-next-line complexity
export const getStatusClass = (status: string): string => {
  switch (status) {
    case PROGRESS_STATUS.NOT_STARTED:
      return 'px-2 py-1 rounded-full bg-gray-100 text-gray-800 text-xs flex items-center gap-1'
    case PROGRESS_STATUS.IN_PROGRESS:
      return 'px-2 py-1 rounded-full bg-blue-100 text-blue-800 text-xs flex items-center gap-1'
    case PROGRESS_STATUS.PASSED:
    case PROGRESS_STATUS.FIRST_REGULAR_MOCK_PASSED:
    case PROGRESS_STATUS.SECOND_REGULAR_MOCK_PASSED:
    case PROGRESS_STATUS.ADDITIONAL_REGULAR_MOCK_PASSED:
    case PROGRESS_STATUS.MAIN_MOCK_PASSED:
      return 'px-2 py-1 rounded-full bg-lime-100 text-lime-800 text-xs flex items-center gap-1'
    case PROGRESS_STATUS.FAILED:
    case PROGRESS_STATUS.FIRST_REGULAR_MOCK_DIDNT_PASS:
    case PROGRESS_STATUS.SECOND_REGULAR_MOCK_DIDNT_PASS:
    case PROGRESS_STATUS.ADDITIONAL_REGULAR_MOCK_DIDNT_PASS:
    case PROGRESS_STATUS.MAIN_MOCK_DIDNT_PASS:
      return 'px-2 py-1 rounded-full bg-red-100 text-red-800 text-xs flex items-center gap-1'
    case PROGRESS_STATUS.FIRST_REGULAR_MOCK_SCHEDULED:
    case PROGRESS_STATUS.SECOND_REGULAR_MOCK_SCHEDULED:
    case PROGRESS_STATUS.ADDITIONAL_REGULAR_MOCK_SCHEDULED:
    case PROGRESS_STATUS.MAIN_MOCK_SCHEDULED:
      return 'px-2 py-1 rounded-full bg-blue-100 text-blue-800 text-xs flex items-center gap-1'
    default:
      return 'px-2 py-1 rounded-full bg-gray-100 text-gray-800 text-xs flex items-center gap-1'
  }
} 